// components/EmailCheck.tsx
import { useState, useEffect } from 'react'
import api from '../utils/api'

interface EmailCheckProps {
  email: string
  onStatusChange: (isAvailable: boolean) => void
  onChecking: (checking: boolean) => void
}

export default function EmailCheck({ email, onStatusChange, onChecking }: EmailCheckProps) {
  const [message, setMessage] = useState('')
  const [status, setStatus] = useState<'idle' | 'checking' | 'available' | 'unavailable' | 'error'>('idle')
  const [debounceTimeout, setDebounceTimeout] = useState<NodeJS.Timeout>()

  useEffect(() => {
    // Clear previous timeout
    if (debounceTimeout) {
      clearTimeout(debounceTimeout)
    }

    // Reset if email is empty
    if (!email || email.trim() === '') {
      setMessage('')
      setStatus('idle')
      onStatusChange(true)
      onChecking(false)
      return
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(email)) {
      setMessage('Please enter a valid email address')
      setStatus('error')
      onStatusChange(false)
      onChecking(false)
      return
    }

    // Set checking state
    setStatus('checking')
    setMessage('Checking availability...')
    onChecking(true)

    // Debounce API call
    const timeout = setTimeout(async () => {
      try {
        const result = await api.checkEmail(email)
        
        if (result.available) {
          setStatus('available')
          setMessage(result.message || '✓ Email is available')
          onStatusChange(true)
        } else {
          setStatus('unavailable')
          setMessage(result.message || '✗ This email is already registered')
          onStatusChange(false)
        }
      } catch (error) {
        console.error('Email check error:', error)
        setStatus('error')
        setMessage('Could not verify email availability')
        // Allow signup to proceed but with a warning
        onStatusChange(true)
      } finally {
        onChecking(false)
      }
    }, 500)

    setDebounceTimeout(timeout)

    // Cleanup
    return () => {
      if (debounceTimeout) {
        clearTimeout(debounceTimeout)
      }
    }
  }, [email, onStatusChange, onChecking])

  // Don't show anything if idle or no email
  if (status === 'idle' || !email) {
    return null
  }

  // Get appropriate styles based on status
  const getStatusStyles = () => {
    switch (status) {
      case 'checking':
        return 'text-gray-500'
      case 'available':
        return 'text-green-600'
      case 'unavailable':
        return 'text-red-600'
      case 'error':
        return 'text-yellow-600'
      default:
        return 'text-gray-500'
    }
  }

  // Get icon based on status
  const getIcon = () => {
    switch (status) {
      case 'checking':
        return (
          <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        )
      case 'available':
        return (
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        )
      case 'unavailable':
        return (
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        )
      case 'error':
        return (
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        )
      default:
        return null
    }
  }

  return (
    <div className={`flex items-center gap-1 text-sm mt-1 ${getStatusStyles()}`}>
      {getIcon()}
      <span>{message}</span>
      {status === 'unavailable' && (
        <button
          type="button"
          onClick={() => {
            // Find and click the login tab/button
            const loginButton = document.querySelector('[data-testid="login-tab"]') as HTMLElement
            if (loginButton) {
              loginButton.click()
            }
          }}
          className="ml-2 text-indigo-600 hover:text-indigo-700 font-medium underline text-xs"
        >
          Sign in instead?
        </button>
      )}
    </div>
  )
}